import {AccountAvatar, AccountEditor} from "./components/chat/AccountEntry";
import './App.css';
import {createBrowserRouter, Navigate, RouterProvider} from "react-router-dom";
import {Button, Nav, NavDropdown} from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import ManageJob from "./components/ManageJob";
import ManageUser from "./components/ManageUser";
import Scheduler from "./components/Scheduler";
import UserDetails from "./components/UserDetails";
import AddUser from "./components/AddUser";
import GuestyIntegration from "./components/GuestyIntegration";
import GuestyMessageHistory from "./components/GuestyMessageHistory";
import Login from "./components/Login";
import {isValidLogin, refreshSession, revokeCurrentSession} from "./service/authService";
import UpdateJob from "./components/UpdateJob";
import ReportLostProperties from "./components/ReportLostProperties";
import {lazy, Suspense, useEffect, useState} from "react";
import {jwtDecode} from "jwt-decode";
import ManageProperty from "./components/ManageProperty";
import JobSupervising from "./components/JobSupervising";
import AccountantPage from "./components/accountant/AccountantPage";
import {UI_VERSION} from "./lib/uiVersion";
import ChatNavItem from "./components/chat/ChatNavItem";
import ChatPage from "./components/chat/ChatPage";
import {ChatProvider} from "./components/chat/ChatProvider";
import {disableWebPushForLogout} from "./components/chat/notifications/useWebPushNotifications";
import JobTypeSettings from "./components/settings/JobTypeSettings";
import WorkerSettings from "./components/workerProfile/WorkerSettings";
import JobComplaintsPage from "./components/jobcomplaint/JobComplaintsPage";
import SessionAdminPage from "./components/SessionAdminPage";
import {startSessionLifecycle} from "./service/sessionLifecycle";

const ChatSettings = lazy(() => import("./components/settings/ChatSettings"));

const router = createBrowserRouter([
    {path: "/", element: <Scheduler />},
    {path: "/login", element: <Login />},
    {path: "/scheduler", element: <Scheduler />},
    {path: "/job-supervising", element: <JobSupervising />},
    {path: "/manage-job", element: <ManageJob />},
    {path: "/manage-job/update-job/:cos_job_id", element: <UpdateJob />},
    {path: "/manage-user", element: <ManageUser />},
    {path: "/manage-user/sessions", element: <SessionAdminPage />},
    {path: "/report-lost-properties", element: <ReportLostProperties />},
    {path: "/manage-user/user-details/:username", element: <UserDetails />},
    {path: "/manage-user/add-user", element: <AddUser />},
    {path: "/manage-user/guesty-integration/:cosCustomerId", element: <GuestyIntegration />},
    {path: "/manage-property", element: <ManageProperty />},
    {path: "/worker-settings", element: <WorkerSettings />},
    {path: "/settings/job-types", element: <JobTypeSettings />},
    {path: "/settings/chat", element: <Navigate to="/settings/workgroups" replace />},
    {path: "/settings/workgroups", element: <Suspense fallback={<div className="p-4" role="status">Loading workgroup management…</div>}><ChatSettings /></Suspense>},
    {path: "/settings/account", element: <Navigate to="/" replace />},
    {path: "/guesty-messages", element: <GuestyMessageHistory />},
    {path: "/accountant", element: <AccountantPage />},
    {path: "/job-complaints", element: <JobComplaintsPage />},
    {path: "/chat", element: <ChatPage />},
    {path: "/chat/:conversation_id", element: <ChatPage />}
]);

function navigateSettings(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    router.navigate(event.currentTarget.getAttribute('href'));
}

function App(props) {
    const [editingName, setEditingName] = useState(false);
    const [authReady, setAuthReady] = useState(false);
    const [sessionVersion, setSessionVersion] = useState(0);

    useEffect(() => {
        let active = true;
        const restore = async (force) => {
            await refreshSession(force);
            if (active) {
                setAuthReady(true);
                setSessionVersion(value => value + 1);
            }
        };
        const stop = startSessionLifecycle(restore);
        return () => {
            active = false;
            stop();
        };
    }, []);

    useEffect(() => {
        let currentUser = getCurrentUserInfo()
        if (currentUser?.cosCustomerName) {
            window.newrelic?.setUserId(currentUser.cosCustomerName)
        }
    }, [authReady, sessionVersion]);

    function getCurrentUserInfo() {
        let cosToken = localStorage.getItem('cosToken')
        if (cosToken == null) return {}
        try {
            let decodedToken = jwtDecode(cosToken);
            return decodedToken?.optinestUser?.optinestApiUser
        } catch (_) {
            return {}
        }
    }

    if (!authReady) {
        return <div className="App"><div className="p-4 text-muted">Restoring session…</div></div>;
    }

    return (
        <div className="App">
            {isValidLogin('ADMIN') ? <ChatProvider key={localStorage.getItem('cosSessionId') || localStorage.getItem('cosSessionEpoch') || 'legacy'}>
                    <Nav defaultActiveKey="/scheduler" as="ul">
                        <Nav.Item as="li">
                            <img width={"100px"} height={"38px"} style={{marginLeft: "10px"}}
                                 src={"/" + process.env.PUBLIC_URL + "UrbanAideBanner.svg"} alt={"UrbanAide Logo"}/>
                        </Nav.Item>
                        <Nav.Item as="li"><Nav.Link href="/scheduler">AirBnB Scheduler</Nav.Link></Nav.Item>
                        <Nav.Item as="li"><Nav.Link href="/job-supervising">Supervising</Nav.Link></Nav.Item>
                        <Nav.Item as="li"><Nav.Link href="/manage-job">Job Management</Nav.Link></Nav.Item>
                        <NavDropdown title="Users" id="users-dropdown">
                            <NavDropdown.Item href="/manage-user">User Management</NavDropdown.Item>
                            <NavDropdown.Item href="/manage-user/sessions">Login Sessions</NavDropdown.Item>
                        </NavDropdown>
                        <Nav.Item as="li"><Nav.Link href="/manage-property">Property Management</Nav.Link></Nav.Item>
                        <Nav.Item as="li"><Nav.Link href="/accountant">Accountant</Nav.Link></Nav.Item>
                        <ChatNavItem />
                        <NavDropdown title="Settings" id="settings-dropdown">
                            <NavDropdown.Item href="/settings/job-types" onClick={navigateSettings}>Job Types</NavDropdown.Item>
                            <NavDropdown.Item href="/worker-settings" onClick={navigateSettings}>Worker Settings</NavDropdown.Item>
                            <NavDropdown.Item href="/settings/workgroups" onClick={navigateSettings}>Workgroup management</NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown title="Report" id="nav-dropdown">
                            <NavDropdown.Item href="/report-lost-properties">Lost Properties</NavDropdown.Item>
                            <NavDropdown.Item href="/job-complaints">Job Complaints</NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown title="Integration" id="integration-dropdown">
                            <NavDropdown.Item href="/guesty-messages">Guesty Messages</NavDropdown.Item>
                        </NavDropdown>
                        <Nav.Item as="li"><AccountAvatar initial={getCurrentUserInfo()?.initial} expanded={editingName} onClick={() => setEditingName(value => !value)} /></Nav.Item>
                        <Nav.Item as="li">
                            <span className="text-muted small d-inline-block"
                                  style={{height: "36px", lineHeight: "36px", margin: "4px 0"}}>{UI_VERSION}</span>
                        </Nav.Item>
                        <Nav.Item as="li">
                            <Button variant="secondary" size={"sm"}
                                    style={{height: "36px", margin: "4px"}}
                                    data-leave-page
                                    onClick={async () => {
                                        await disableWebPushForLogout().catch(() => undefined);
                                        await revokeCurrentSession();
                                        window.location.href = "/login"
                                    }}
                            >Logout</Button>
                        </Nav.Item>
                    </Nav>
                    <hr style={{margin: "0px"}}/>
                    {editingName ? <AccountEditor onClose={() => setEditingName(false)} /> : null}
                    <RouterProvider router={router} />
                </ChatProvider>
                : <Login/>}
        </div>);
}

export default App;
